'use client';

import { useEffect, useState } from 'react';

import MockTest from '../mock-test';
import { useNavigate } from 'react-router-dom';
import { getMockTests, getTagList, IMockTest } from '@app/api/mockTest.api';
import { Pagination } from '@app/components/common/Pagination/Pagination';
import { notificationController } from '@app/controllers/notificationController';
import { useTranslation } from 'react-i18next';
import SectionTabs from './SectionTabs';
import { Spin } from 'antd';
import { MOCK_TEST_TYPE } from '@app/constants/mockTest';

interface IMockTestListProps {
  type: string;
}

const MockTestList: React.FC<IMockTestListProps> = ({ type }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mockTests, setMockTests] = useState<IMockTest[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pageSize, setPageSize] = useState(20);
  const [totalData, setTotalData] = useState(0);
  const [activeSection, setActiveSection] = useState('');
  // const [tabType, setTabType] = useState('');
  const [sectionTabs, setSectionTabs] = useState<{ name: string; key: string }[]>([]);

  useEffect(() => {
    // setTabType(type);
    setCurrentPage(1);
    setActiveSection('');
  }, [type]);

  useEffect(() => {
    fetchTags();
    fetchMockTest();
  }, [type, activeSection, currentPage, pageSize]);

  const fetchMockTest = async () => {
    setLoading(true);
    try {
      const res = await getMockTests(currentPage, pageSize, type, activeSection);
      setMockTests(res.data);
      setTotalData(res.meta.itemCount);
    } catch (error) {
      notificationController.error({
        message: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      if (type === MOCK_TEST_TYPE.QUESTION_TEST) {
        const res = await getTagList();
        setSectionTabs([{ name: t('common.all'), key: '' }, ...res.tags]);
      } else if (type === MOCK_TEST_TYPE.SECTION_TEST) {
        setSectionTabs([
          { name: t('common.all'), key: '' },
          { name: t('common.speaking'), key: 'Speaking' },
          { name: t('common.writing'), key: 'Writing' },
          { name: t('common.reading'), key: 'Reading' },
          { name: t('common.listening'), key: 'Listening' },
        ]);
      }
    } catch (error) {
      notificationController.error({
        message: error,
      });
    }
  };

  const handleTestClick = (id: string) => {
    navigate(`/exam/mock-test?id=${id}`);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center">
        <div className="w-full max-w-4xl">
          {type === MOCK_TEST_TYPE.SECTION_TEST && (
            <div className="flex">
              <SectionTabs tabs={sectionTabs} activeTab={activeSection} onTabClick={handleSectionChange} />
            </div>
          )}
          {type === MOCK_TEST_TYPE.QUESTION_TEST && (
            <div className="flex flex-wrap">
              <SectionTabs tabs={sectionTabs} activeTab={activeSection} onTabClick={handleSectionChange} />
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center">
              <Spin size="small" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {mockTests.map((test: IMockTest, index) => (
                  <MockTest
                    key={index}
                    title={test.name}
                    status={test.status}
                    year={test.year}
                    description={t(`page.mockTest.NOTE_${type}`)}
                    duration={`2-${t('page.mockTest.hour')}`}
                    onClick={() => handleTestClick(test.id)}
                  />
                ))}
              </div>
              <Pagination
                className="text-center"
                showSizeChanger={false}
                defaultCurrent={currentPage}
                total={totalData}
                pageSize={pageSize}
                onChange={handlePageChange}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MockTestList;
